'use client'
import React from 'react'

import { AiProvider, useChat } from '@hanzo/ai/react'
import { Chat } from '@hanzo/ui/chat'
import { Box, cn } from '@hanzo/ui'
import { Logo as Mark } from '@luxfi/logo'

import type { ChatbotSuggestedQuestion } from '../types'

const Thread: React.FC<{
  greeting: string
  questions: ChatbotSuggestedQuestion[]
}> = ({ greeting, questions }) => {
  const { messages, streaming, send, stop } = useChat({ model: 'enso' })

  return (
    <Chat
      messages={messages}
      streaming={streaming}
      send={send}
      stop={stop}
      composer={{ placeholder: 'Ask about cryptography, hardware or the network' }}
      empty={(
        <Box className='flex flex-col flex-1 gap-3 p-5 overflow-y-auto'>
          <Box tag='p' className='text-sm text-neutral-300 leading-relaxed'>{greeting}</Box>
          <Box className='text-xs text-neutral-500 mt-2'>Try asking</Box>
          <Box className='flex flex-col gap-2'>
            {questions.map(({ heading, message }) => (
              <Box
                tag='button'
                key={message}
                onClick={() => { send(message) }}
                className='w-full text-left p-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] hover:border-white/20 cursor-pointer flex flex-col gap-1'
              >
                <Box className='text-xs font-semibold text-white'>{heading}</Box>
                <Box className='text-[11px] text-neutral-400'>{message}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    />
  )
}

const ChatWidget: React.FC<{
  title: string
  subtitle?: string
  greeting?: string
  buttonClx?: string
  suggestedQuestions?: ChatbotSuggestedQuestion[]
}> = ({
  title = 'LUX',
  subtitle = 'AI',
  greeting = 'Ask about post-quantum cryptography, the network, telecom links or the hardware.',
  suggestedQuestions = [],
  buttonClx = ''
}) => {
  const [open, setOpen] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (<>
    <Box role='dialog' aria-modal='true' aria-label={title} className={cn(
      'fixed z-[400] bottom-0 right-0 w-full h-full sm:bottom-24 sm:right-6 sm:w-[420px] sm:h-[580px] sm:max-h-[85vh]',
      'rounded-2xl bg-black/90 backdrop-blur-2xl border border-white/[0.12] overflow-hidden shadow-2xl',
      open ? 'flex flex-col' : 'hidden'
    )}>
      <Box className='flex flex-row px-5 h-14 border-b border-white/[0.08] items-center justify-between'>
        <Box className='flex flex-row items-center gap-2.5'>
          <Mark variant='white' size={20} />
          <Box tag='h3' className='font-heading text-sm text-white'>{title}</Box>
          <Box tag='span' className='text-cyan-400 font-mono text-xs'>{subtitle}</Box>
        </Box>
        <Box
          tag='button'
          aria-label='Close'
          onClick={() => { setOpen(false) }}
          className='p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/10 cursor-pointer text-xs font-mono'
        >
          ✕
        </Box>
      </Box>

      <Box className='flex flex-col flex-1 w-full min-w-0 overflow-hidden'>
        {open && (
          <AiProvider>
            <Thread greeting={greeting} questions={suggestedQuestions} />
          </AiProvider>
        )}
      </Box>
    </Box>

    <Box
      tag='button'
      onClick={() => { setOpen(!open) }}
      aria-label={`${title} ${subtitle ?? ''}`.trim()}
      aria-expanded={open}
      className={cn(
        'fixed bottom-6 right-6 z-[390] w-14 h-14 flex items-center justify-center cursor-pointer',
        'opacity-80 hover:opacity-100 hover:scale-105 transition duration-200',
        buttonClx
      )}
    >
      <Mark variant='white' size={26} />
    </Box>
  </>)
}

export default ChatWidget
